const ALGORITHM = 'RSA-OAEP';
const HASH = 'SHA-256';

function pemToArrayBuffer(pem: string): Uint8Array {
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/g, '')
    .replace(/-----END PUBLIC KEY-----/g, '')
    .replace(/\\n/g, '')
    .replace(/\n/g, '')
    .replace(/\r/g, '')
    .replace(/\s+/g, '');

  if (typeof window === 'undefined' && typeof Buffer !== 'undefined') {
    const buf = Buffer.from(b64, 'base64');
    return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  }

  const binStr = atob(b64);
  const bytes = new Uint8Array(binStr.length);
  for (let i = 0; i < binStr.length; i++) {
    bytes[i] = binStr.charCodeAt(i);
  }
  return bytes;
}

function getSubtleCrypto(): SubtleCrypto | undefined {
  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    return window.crypto.subtle;
  }
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.subtle) {
    return globalThis.crypto.subtle;
  }
  return undefined;
}

function getPublicKeyPem(): string | undefined {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PASSWORD_ENCRYPTION_PUBLIC_KEY) {
    return import.meta.env.VITE_PASSWORD_ENCRYPTION_PUBLIC_KEY;
  }
  if (typeof process !== 'undefined' && process.env?.VITE_PASSWORD_ENCRYPTION_PUBLIC_KEY) {
    return process.env.VITE_PASSWORD_ENCRYPTION_PUBLIC_KEY;
  }
  return undefined;
}

let cachedKey: CryptoKey | null = null;

async function getKey(): Promise<CryptoKey | null> {
  if (cachedKey) return cachedKey;

  const rawKey = getPublicKeyPem();
  if (!rawKey) {
    return null;
  }

  try {
    const keyBytes = pemToArrayBuffer(rawKey);
    const subtle = getSubtleCrypto();
    if (!subtle) return null;

    cachedKey = await subtle.importKey(
      'spki',
      keyBytes as BufferSource,
      { name: ALGORITHM, hash: HASH },
      false,
      ['encrypt'],
    );
    return cachedKey;
  } catch (err) {
    console.error('[passwordEncrypt] Failed to import public key:', err);
    return null;
  }
}

/**
 * Encrypts a plaintext password using RSA-OAEP (SHA-256).
 * Gracefully falls back to plaintext if key is missing or encryption fails.
 */
export async function encryptPassword(
  plaintext: string | undefined | null,
): Promise<string> {
  if (!plaintext) return '';

  const key = await getKey();
  if (!key) return plaintext;

  try {
    const subtle = getSubtleCrypto();
    if (!subtle) return plaintext;

    const encoded = new TextEncoder().encode(plaintext);
    const encrypted = await subtle.encrypt(
      { name: ALGORITHM },
      key,
      encoded as BufferSource,
    );

    const bytes = new Uint8Array(encrypted);
    let binStr = '';
    for (let i = 0; i < bytes.length; i++) {
      binStr += String.fromCharCode(bytes[i]);
    }
    return btoa(binStr);
  } catch (err) {
    console.error('[passwordEncrypt] Encryption failed, sending plaintext fallback:', err);
    return plaintext;
  }
}
